/* eslint-disable no-unused-vars */
/* eslint-disable operator-linebreak */

/* eslint-disable implicit-arrow-linebreak */
import React, { useEffect, useState, useMemo } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';

import { DefaultTable } from 'src/components';

import { useTypedSelector } from 'src/hooks/useTypedSelector';

import { IBenefitListTable } from 'src/interfaces';

import { CustomCell, TooltipBoxTable } from 'src/components/tooltip-box-table';
import { DataTableCellDeleteButton, DataTableCellAddButton } from 'pg-ui-library';
import { Container } from './styles';
import { useEmployeeBenefits } from '../../queries/employee.benefits.query';
import { useRemoveBenefitFromEmployee } from '../../mutations/employee-remove-benefit.mutation';
import { useAddBenefitToEmployee } from '../../mutations/employee-add-benefit.mutation';

export const benefitsListInitialColumns: TableColumn<IBenefitListTable>[] = [
  {
    name: 'Benefit Id',
    selector: (row: IBenefitListTable) => row.benefitId || '',
    sortField: 'benefitID',
    width: '180px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Plan',
    selector: (row: IBenefitListTable) => row.plan || '',
    sortField: 'plan',
    width: '180px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Plan Description',
    selector: (row: IBenefitListTable) => row.planDescription || '',
    width: '180px',
    sortField: 'planDescription',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Option',
    selector: (row: IBenefitListTable) => row.option || '',
    width: '180px',
    sortField: 'option',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Option Description',
    selector: (row: IBenefitListTable) => row.optionDescription || '',
    width: '220px',
    sortField: 'optionDescription',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Type',
    selector: (row: IBenefitListTable) => row.type || '',
    width: '180px',
    sortField: 'type',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Rates',
    selector: (row: IBenefitListTable) => row.rates.value,
    cell: ({ rates }: IBenefitListTable) => (
      <CustomCell value={rates.value}>
        <TooltipBoxTable title="Rates" columns={rates.columns} rows={rates.rows} />
      </CustomCell>
    ),
    wrap: true,
  },
  {
    name: 'Status',
    selector: (row: IBenefitListTable) => (row.status ? 'Active' : 'Inactive'),
    width: '180px',
    sortField: 'status',
    omit: false,
    sortable: false,
    reorder: false,
    wrap: true,
  },
  {
    name: 'Pending to sync',
    selector: (row: IBenefitListTable) => (row.waitingSync ? 'Yes' : 'No'),
    width: '180px',
    sortField: 'status',
    omit: false,
    sortable: false,
    reorder: false,
    wrap: true,
  },
];

export const BenefitsListTable: React.FC<{
  searchTerm: string;
  activeBenefits: boolean;
  columns: TableColumn<IBenefitListTable>[];
}> = ({ searchTerm, columns, activeBenefits }) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const companyId = useTypedSelector(state => state.config.data?.config?.selectedCompanyId) || '';

  const { selectedEmployee } = useTypedSelector(state => state.employees.data);

  const personnelNumber = selectedEmployee.employee?.employeeId || '';

  const {
    data: benefitsItems,
    refetch,
    isLoading,
    isFetching,
  } = useEmployeeBenefits(personnelNumber, {
    page,
    companyId,
    activeBenefits,
    limit,
    search: searchTerm,
    sortBy,
    sortDirection,
  });

  useEffect(() => {
    refetch();
  }, [searchTerm, page, limit, sortBy, sortDirection, activeBenefits]);

  const handlePageChange = (nPage: number) => {
    setPage(nPage);
  };

  const handlePerRowsChange = async (newLimit: number, nPage: number) => {
    setPage(nPage);
    setLimit(newLimit);
  };

  const handleSort = async (column: TableColumn<IBenefitListTable>, newSortDirection: SortOrder) => {
    setSortBy(column.sortField as unknown as string);
    setSortDirection(newSortDirection);
  };

  const removeBenefitFromEmployee = useRemoveBenefitFromEmployee();

  const addBenefitFromEmployee = useAddBenefitToEmployee();

  const confirmDeleteAction = (row: IBenefitListTable) => {
    if (
      // eslint-disable-next-line no-alert
      window.confirm(`Removing ${row.plan} from employee. Confirm?`) === true
    ) {
      if (row.benefitId) {
        removeBenefitFromEmployee.mutate(row.benefitId);
      }
    }
  };

  const confirmAddAction = (row: IBenefitListTable) => {
    if (
      // eslint-disable-next-line no-alert
      window.confirm(`Adding ${row.plan} to employee benefits. Confirm?`) === true
    ) {
      if (row.benefitId) {
        removeBenefitFromEmployee.mutate(row.benefitId);
      }
    }
  };

  const deleteButtonWrapper = (onClick: Function) =>
    activeBenefits &&
    !removeBenefitFromEmployee.isLoading && (
      <DataTableCellDeleteButton size={30} onClick={() => onClick()} cursor="pointer" />
    );

  const addButtonWrapper = (onClick: Function) =>
    !activeBenefits &&
    !addBenefitFromEmployee.isLoading && (
      <DataTableCellAddButton size={30} onClick={() => onClick()} cursor="pointer" />
    );

  /**
  const columnsAndActions = useMemo(
    () => [
      ...columns,
      {
        cell: (row: IBenefitListTable) =>
          addButtonWrapper(() => confirmAddAction(row)),
      },
      {
        cell: (row: IBenefitListTable) =>
          deleteButtonWrapper(() => confirmDeleteAction(row)),
      },
    ],
    [activeBenefits],
  );
   */

  return (
    <Container>
      <DefaultTable
        columns={columns}
        data={benefitsItems?.items || []}
        progressPending={isLoading || isFetching}
        pagination
        paginationServer
        paginationTotalRows={benefitsItems?.meta.totalItems || 0}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        onSort={handleSort}
        sortServer
      />
    </Container>
  );
};
